import React, { useEffect, useState } from "react";
import {
    FaArrowLeft,
    FaCalendarAlt,
    FaFileExcel,
    FaFilePdf,
    FaFileWord,
    FaImage,
    FaPaperclip,
    FaPlay,
    FaUsers,
} from "react-icons/fa";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import FilePreviewModal from "../../Modal/AttachmentPreview";
import { getUserAvatar } from "../../utils/user-helper";
import { formatDateTime } from "../../utils/date";

import Report from "../../models/Report";
import { capitalizeWords } from "../../utils/string";

const ProjectReportDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const reportData = location.state as Report;

    const [report] = useState<Report | null>(reportData);
    const [viewerMedia, setViewerMedia] = useState<File | null>(null);

    const goBack = () => {
        navigate("/project-reports");
    };

    // ATTACHMENT ICON
    const getAttachmentIcon = (item: File) => {
        if (item.type.endsWith("pdf")) return FaImage({});
        if (item.type.endsWith("mp4")) return FaPlay({});
        if (item.type.endsWith("pdf")) return FaFilePdf({});
        if (item.type.endsWith("docx")) return FaFileWord({});
        if (item.type.endsWith("xlsx")) return FaFileExcel({});

        return FaPaperclip({});
    };

    // ATTACHMENT STYLE
    const getAttachmentStyle = (item: File) => {
        switch (item.type) {
            case ".jpg":
                return "bg-blue-50 text-blue-600";

            case ".mp4":
                return "bg-purple-50 text-purple-600";

            case ".pdf":
                return "bg-red-50 text-red-600";

            case ".docx":
                return "bg-indigo-50 text-indigo-600";

            case ".xlsx":
                return "bg-emerald-50 text-emerald-600";

            default:
                return "bg-slate-100 text-slate-600";
        }
    };

    if (!report) {
        return (
            <div className="min-h-screen bg-slate-50 px-4 py-16">
                <div className="mx-auto max-w-lg rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">

                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                        {FaPaperclip({ className: "text-2xl" })}
                    </div>

                    <h2 className="mt-5 text-lg font-bold text-slate-900">
                        Report Not Found
                    </h2>

                    <p className="mt-2 text-sm text-slate-500">
                        This report may have been removed or you may not have
                        permission to access it.
                    </p>

                    <button
                        onClick={goBack}
                        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700"
                    >
                        {FaArrowLeft({})}
                        Back to Reports
                    </button>

                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            <main className="mx-auto max-w-[1250px] px-4 py-6 md:px-6 md:py-8">

                {/* PAGE HEADER */}
                <div className="mb-7 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">

                    {/* LEFT: BACK BUTTON & TITLE */}
                    <div className="flex min-w-0 items-start gap-3">

                        <button
                            type="button"
                            onClick={() => navigate("/project-reports")}
                            className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                        >
                            {FaArrowLeft({
                                size: 13,
                            })}
                        </button>

                        <div className="min-w-0">
                            <h1 className="text-xl font-bold tracking-tight text-slate-800 sm:text-2xl">
                                Report Details
                            </h1>

                            <p className="mt-1 truncate text-sm text-slate-400">
                                Detailed overview of the project report for {capitalizeWords(report.site?.name)}.
                            </p>
                        </div>

                    </div>

                    {/* RIGHT: SUBMISSION DATE */}
                    <div className="flex shrink-0 items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 sm:min-w-[210px]">

                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                            {FaCalendarAlt({
                                size: 16,
                            })}
                        </div>

                        <div className="min-w-0">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                Submitted On
                            </p>

                            <p className="mt-1 text-xs font-bold text-slate-700">
                                {formatDateTime(report.createdAt)}
                            </p>
                        </div>

                    </div>

                </div>

                {/* MAIN CONTENT */}
                <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">

                    {/* LEFT COLUMN */}
                    <div className="min-w-0 space-y-6">

                        {/* REPORT CONTENT */}
                        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">

                            {/* AUTHOR */}
                            <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 p-5 md:p-6">

                                <img
                                    src={getUserAvatar(report.user)}
                                    className="h-12 w-12 rounded-full object-cover"
                                    alt=""
                                />

                                <div className="min-w-0">
                                    <h3 className="truncate text-sm font-bold text-slate-900">
                                        {report.user.firstName} {report.user.lastName}
                                    </h3>

                                    <p className="mt-0.5 text-xs capitalize text-slate-400">
                                        {report.user.portfolio}
                                    </p>
                                </div>

                                <div className="ml-auto">
                                    <span className="rounded-lg bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-600">
                                        {report.attachments?.length || 0} Attachments
                                    </span>
                                </div>

                            </div>

                            {/* DESCRIPTION */}
                            <div className="p-5 md:p-7">

                                <div className="mb-5 flex flex-wrap items-center gap-2">

                                    {/* PROJECT SITE */}
                                    <span className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-semibold capitalize text-blue-600">
                                        {report.site?.name}
                                    </span>

                                    {/* REPORT CATEGORY */}
                                    <span className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-600 capitalize">
                                        {report.category || "General Report"}
                                    </span>

                                </div>

                                <h2 className="text-lg font-bold text-slate-900">
                                    Report Description
                                </h2>

                                <p className="mt-3 whitespace-pre-line break-words text-sm leading-7 text-slate-600">
                                    {report.description}
                                </p>

                            </div>

                            {/* ATTACHMENTS */}
                            {report.attachments?.length > 0 && (
                                <div className="border-t border-slate-100 p-5 md:p-7">

                                    <div className="mb-5 flex items-center justify-between gap-3">

                                        <div>
                                            <h3 className="font-bold text-slate-900">
                                                Project Media & Documents
                                            </h3>

                                            <p className="mt-1 text-xs text-slate-400">
                                                Supporting files submitted with this report.
                                            </p>
                                        </div>

                                        <span className="flex h-9 min-w-9 items-center justify-center rounded-lg bg-slate-100 px-2 text-sm font-bold text-slate-600">
                                            {report.attachments.length}
                                        </span>

                                    </div>

                                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-3 xl:grid-cols-4">

                                        {report.attachments.map(({ file, id }) => (
                                            <button
                                                type="button"
                                                key={id}
                                                onClick={() => setViewerMedia(file)}
                                                className="group min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white text-left transition hover:border-blue-300 hover:shadow-md"
                                            >

                                                {/* IMAGE */}
                                                {file.type === ".jpg" || file.type === ".png" ? (

                                                    <div className="relative aspect-square overflow-hidden bg-slate-100">

                                                        <img
                                                            src={file.url}
                                                            alt={file.name || "Report image"}
                                                            className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                                                        />

                                                        <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition group-hover:bg-black/20">

                                                            <span className="flex h-10 w-10 scale-75 items-center justify-center rounded-full bg-white text-blue-600 opacity-0 shadow-lg transition group-hover:scale-100 group-hover:opacity-100">
                                                                {FaImage({})}
                                                            </span>

                                                        </div>

                                                    </div>

                                                ) : file.type === ".mp4" ? (

                                                    <div className="relative aspect-square overflow-hidden bg-slate-900">

                                                        <video
                                                            src={file.url}
                                                            className="h-full w-full object-cover"
                                                            preload="metadata"
                                                            muted
                                                        />

                                                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">

                                                            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-blue-600 shadow-lg">
                                                                {FaPlay({ className: "text-sm" })}
                                                            </span>

                                                        </div>

                                                        <span className="absolute bottom-2 left-2 rounded-md bg-black/60 px-2 py-1 text-[10px] font-bold text-white">
                                                            VIDEO
                                                        </span>

                                                    </div>

                                                ) : (

                                                    <div className="flex aspect-square flex-col items-center justify-center p-3">

                                                        <div className={`flex h-14 w-14 items-center justify-center rounded-2xl text-2xl ${getAttachmentStyle(file)}`}>
                                                            {getAttachmentIcon(file)}
                                                        </div>

                                                        <span className="mt-4 w-full truncate text-center text-xs font-semibold text-slate-700">
                                                            {file.name}
                                                        </span>

                                                        <span className="mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                                            {file.type}
                                                        </span>

                                                    </div>

                                                )}

                                                {/* FILE NAME / ACTION */}
                                                <div className="border-t border-slate-100 px-3 py-2.5"
                                                    onClick={() => {
                                                        // console.log(item)
                                                        setViewerMedia(file);
                                                    }}
                                                >

                                                    <p className="truncate text-[11px] font-semibold text-slate-700">
                                                        {file.name}
                                                    </p>

                                                    <p className="mt-1 text-[10px] font-medium text-blue-600">
                                                        Click to preview
                                                    </p>

                                                </div>

                                            </button>

                                        ))}

                                    </div>

                                </div>
                            )}

                        </section>

                    </div>

                    {/* RIGHT COLUMN */}
                    <aside className="space-y-5">

                        {/* REPORT INFORMATION */}
                        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">

                            <div className="flex items-center gap-3">

                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                                    {FaFilePdf({})}
                                </div>

                                <div>
                                    <h3 className="font-bold text-slate-900">
                                        Report Information
                                    </h3>

                                    <p className="text-xs text-slate-400">
                                        Submission overview
                                    </p>
                                </div>

                            </div>

                            <div className="mt-6 space-y-5">

                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                        Project Site
                                    </p>

                                    <p className="mt-1.5 text-sm font-semibold capitalize text-slate-800">
                                        {report.site?.name}
                                    </p>
                                </div>

                                <div className="border-t border-slate-100 pt-4">
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                        Report Category
                                    </p>

                                    <p className="mt-1.5 text-sm font-semibold text-slate-700 capitalize">
                                        {report.category || "General Report"}
                                    </p>
                                </div>

                                <div className="border-t border-slate-100 pt-4">
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                        Submission Date
                                    </p>

                                    <p className="mt-1.5 text-sm font-semibold text-slate-800">
                                        {formatDateTime(report.createdAt)}
                                    </p>
                                </div>

                                <div className="border-t border-slate-100 pt-4">
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                        Submitted By
                                    </p>

                                    <p className="mt-1.5 text-sm font-semibold text-slate-800">
                                        {report.user.firstName} {report.user.lastName}
                                    </p>

                                    <p className="mt-1 text-xs text-slate-400 capitalize">
                                        {report.user.portfolio}
                                    </p>
                                </div>

                                <div className="border-t border-slate-100 pt-4">
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                        Total Attachments
                                    </p>

                                    <p className="mt-1.5 text-sm font-semibold text-slate-800">
                                        {report.attachments?.length || 0} files
                                    </p>
                                </div>

                            </div>

                        </section>

                        {/* NOTIFIED USERS */}
                        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">

                            <div className="border-b border-slate-100 p-5">

                                <div className="flex items-center gap-3">

                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                                        {FaUsers({})}
                                    </div>

                                    <div>
                                        <h3 className="font-bold text-slate-900">
                                            Notified Users
                                        </h3>

                                        <p className="text-xs text-slate-400">
                                            People who received this report
                                        </p>
                                    </div>

                                </div>

                                <div className="mt-4">
                                    <span className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600">
                                        {report.recipients?.length || 0} Recipients
                                    </span>
                                </div>

                            </div>

                            <div className="divide-y divide-slate-100">

                                {report.recipients?.length > 0 ? (

                                    report.recipients.map(({ user }: any) => (

                                        <div
                                            key={user.id}
                                            className="flex min-w-0 items-center gap-3 p-4"
                                        >

                                            <img
                                                src={getUserAvatar(user)}
                                                className="h-10 w-10 shrink-0 rounded-full object-cover"
                                                alt=""
                                            />

                                            <div className="min-w-0 flex-1">

                                                <p className="truncate text-xs font-bold text-slate-800">
                                                    {user.firstName} {user.lastName}
                                                </p>

                                                <p className="mt-1 truncate text-[11px] font-medium capitalize text-blue-600">
                                                    {user.portfolio || "Staff"}
                                                </p>

                                                <p className="mt-1 truncate text-[10px] text-slate-400">
                                                    {user.email}
                                                </p>

                                            </div>

                                        </div>

                                    ))

                                ) : (

                                    <div className="p-6 text-center">

                                        {FaUsers({ className: "mx-auto text-2xl text-slate-300" })}

                                        <p className="mt-3 text-xs text-slate-500">
                                            No recipients found.
                                        </p>

                                    </div>

                                )}

                            </div>

                        </section>

                    </aside>

                </div>

            </main>

            {/* ATTACHMENT PREVIEW */}
            <FilePreviewModal
                file={viewerMedia}
                previewUrl={null}
                onClose={() => setViewerMedia(null)}
            />

        </div>
    );
};

export default ProjectReportDetails;